/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch, useSelector } from 'react-redux';
import {
    PlatformIccidMsisdnIpPoolReducerAction,
    PlatformIccidMsisdnIpPoolReducerType,
    platformIccidMsisdnIpPoolReducer,
} from '../../../modules/iccid/PlatformIccidMsisdnIpPoolReducer';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { apiShortPlatformImsiRange } from '../../../interfaces/collections/PlatformIccidRmoHistory';
import {
    PlatformImsiRange,
    PlatformImsiRangeResponse,
    PlatformImsiRangeResponseCollectionInterface,
} from '../../../interfaces/platform/PlatformImsiRange';
import Filter from '../../../interfaces/forms/Filter';
import { usePlatformResellerHeaders, usePlatformResellerList } from '../../../modules/lib/hooks/usePlatformResellerHooks';
import { NotificationHelper } from '../../../helpers/NotificationHelper';

type ImsiChangeOwnerType = {};

export const ImsiChangeOwner: FunctionComponent<ImsiChangeOwnerType> = (): React.ReactElement => {
    const { openImsiChangeOwner, valueChange, selectedValue } = useSelector(
        (state: { platformIccidMsisdnIpPoolReducer: PlatformIccidMsisdnIpPoolReducerType }) => state.platformIccidMsisdnIpPoolReducer
    );
    const [open, setOpen] = useState(false);
    const [fromImsi, setFromImsi] = useState<string | null>(null);
    const [toImsi, setToImsi] = useState<string | null>(null);
    const [totalCard, setTotalCard] = useState(0);
    const dispatch = useDispatch();

    const platformResellerList = usePlatformResellerList();
    const platformResellerHeader = usePlatformResellerHeaders(platformResellerList);

    useEffect(() => {
        setOpen(openImsiChangeOwner ?? false);
    }, [openImsiChangeOwner]);

    useEffect(() => {
        if ((fromImsi && fromImsi.length > 0) || (toImsi && toImsi.length > 0)) {
            const itemLoader = new ItemLoaders(dispatch);
            const filters: Array<Filter> = [];
            if (fromImsi && fromImsi.length > 0) {
                filters.push({ name: 'fromImsi', value: fromImsi });
            }
            if (toImsi && toImsi.length > 0) {
                filters.push({ name: 'toImsi', value: toImsi });
            }
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformImsiRange)),
                PlatformIccidMsisdnIpPoolReducerAction.RMO_CHANGE_OPEN_DIALOG_ADVANCED_RANGE_COUNTER,
                filters,
                [],
                (data: PlatformImsiRangeResponseCollectionInterface): void => {
                    setTotalCard(data['hydra:member'][0].rangeCount);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, fromImsi, toImsi]);

    const closeCallback = (): void => {
        dispatch({
            type: PlatformIccidMsisdnIpPoolReducerAction.IMSI_CHANGE_OWNER_OPEN_DIALOG_ADVANCED,
            openImsiChangeOwner: false,
        } as PlatformIccidMsisdnIpPoolReducerType);
    };

    const confirmCallback = (): void => {
        if (!fromImsi || fromImsi.length !== 15) {
            NotificationHelper.notifyError('pools:sim.range_from_imsi_empty_from_number', dispatch);
            return;
        }
        if (!toImsi || toImsi.length !== 15) {
            NotificationHelper.notifyError('pools:sim.range_from_imsi_empty_to_number', dispatch);
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformImsiRange + '/-1')),
            'change_owner_call_api',
            {
                fromImsi: fromImsi,
                toImsi: toImsi ?? fromImsi,
                ownerPlatformResellerId: selectedValue,
            } as PlatformImsiRange,
            (data: PlatformImsiRangeResponse) => {
                dispatch({ type: PlatformIccidMsisdnIpPoolReducerAction.IMSI_CHANGE_OWNER_RELOAD });
                closeCallback();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    return (
        <Dialog open={open} onClose={closeCallback} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                <I18n trans="pools:sim.owner_change" />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_from_imsi"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setFromImsi(event.currentTarget.value);
                            }}
                            value={fromImsi}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_to_imsi"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setToImsi(event.currentTarget.value);
                            }}
                            value={toImsi}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <SimpleSelect
                            dispatchName={PlatformIccidMsisdnIpPoolReducerAction.IMSI_OWNER_CHANGE}
                            label="pools:sim.choose_partner"
                            required={false}
                            elements={platformResellerHeader}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <div style={{ marginTop: '20px' }}>
                            <I18n trans="pools:sim.total_card" valueObject={{ count: totalCard }} />
                        </div>
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={closeCallback} callbackParam={{}} label="general:common.close" size="sm" />
                {selectedValue && fromImsi && (
                    <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="pools:sim.confirm_partner_change" size="sm" />
                )}
            </DialogActions>
        </Dialog>
    );
};
